/*
    Examiner's test session page.

    PIN-210. Session creation used to live under the top-nav "Sessions" entry and
    was battery-agnostic: an empty session was created first, then a cascader
    offered every battery and every test the examiner could reach — including
    rating scales, which produce an examiner-administered session that cannot be
    sent to the rater it was meant for. Creation now starts from the battery, so
    only that battery's examiner-administered tests are on offer.
*/

import React, { useCallback, useEffect, useState } from 'react';
import Dashboard from '../dashboard';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import styles from '../../style/info-card.module.css';
import { Button, Input, Select, notification } from 'antd';
import { fetchBatteryTests, isSessionTest } from '../../utils/battery-kind';
import { useStartSession } from '../../hooks/useStartSession';

const TestSessionPage = () => {
  const { batteryID } = useParams();
  const history = useHistory();
  const token = useSelector((state) => state.token);

  const { isStarting, start } = useStartSession();

  const [examinees, setExaminees] = useState([]);
  const [selectedExamineeID, setSelectedExamineeID] = useState(null);
  const [selectedLabel, setSelectedLabel] = useState('');
  const [selectedTestID, setSelectedTestID] = useState(null);
  const [tests, setTests] = useState([]);

  /*
    Catching the axios rejection and then dereferencing response.data threw a
    TypeError inside an un-awaited effect — an unhandled rejection, with the
    examinee Select silently empty and nothing to tell the examiner why.
  */
  const getExaminees = useCallback(async () => {
    try {
      const response = await axios.get(process.env.REACT_APP_API_URL + '/examinee/directory', {
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Token ' + token
        }
      });

      if (!response || !response.data || !response.data.examinees)
        throw new Error('Could not get examinee directory.');
      setExaminees(response.data.examinees);
    } catch (error) {
      console.log(error);
      notification['error']({
        message: 'Could not load examinees.',
        description: 'Reload the page to try again.'
      });
    }
  }, [token]);

  const getTests = useCallback(async () => {
    const { tests: batteryTests, failed } = await fetchBatteryTests(batteryID, token);
    if (failed) {
      notification['error']({
        message: "Could not load this battery's tests.",
        description: 'Reload the page to try again.'
      });
    }
    setTests(batteryTests);
  }, [batteryID, token]);

  const renderExamineeSelect = useCallback(() => {
    return (
      <Select
        allowClear
        className={styles['item-select']}
        filterOption
        onChange={setSelectedExamineeID}
        optionFilterProp="filtervalue"
        placeholder="Examinee"
        showSearch
      >
        {Object.values(examinees).map((examinee) => {
          return (
            <Select.Option
              filtervalue={`${examinee.firstName} ${examinee.lastName} ${examinee.label}`}
              key={examinee._id}
            >
              {`${examinee.firstName} ${examinee.lastName}`}
            </Select.Option>
          );
        })}
      </Select>
    );
  }, [examinees]);

  const formatTestName = (name) => name.replace(/\((\d+-\d+)\)/g, '(Ages $1)');

  const renderTestSelect = useCallback(() => {
    return (
      <Select
        allowClear
        className={styles['item-select']}
        filterOption
        onChange={setSelectedTestID}
        optionFilterProp="filtervalue"
        placeholder="Test"
        showSearch
      >
        {/* Questionnaire tests are deliberately absent: a rater completes those
            from an invite link, not from an examiner-administered session. */}
        {Object.values(tests)
          .filter(isSessionTest)
          .map((test) => {
            return (
              <Select.Option filtervalue={test.name} key={test._id}>
                {formatTestName(test.name)}
              </Select.Option>
            );
          })}
      </Select>
    );
  }, [tests]);

  const startTest = useCallback(async () => {
    const started = await start({
      examineeID: selectedExamineeID,
      recordLabel: selectedLabel,
      testID: selectedTestID
    });

    // A null result means the hook already surfaced the failure and kept the
    // selections intact; navigating to a session that never started would
    // dead-end the examiner.
    if (started) history.push(`/examiner/test/${started.sessionID}`);
  }, [history, selectedExamineeID, selectedLabel, selectedTestID, start]);

  useEffect(() => {
    getExaminees();
    getTests();
  }, [getExaminees, getTests]);

  return (
    <Dashboard>
      <div className={styles['card']}>
        <div className={styles['header']}>
          <label className={styles['name']}>Create Test Session</label>
        </div>
        <hr className={styles['horizontal-line']}></hr>
        <div className={styles['info-container']}>
          <div className={styles['item-container']}>
            <label className={styles['item-name']}>Examinee</label>
            {renderExamineeSelect()}
            <hr className={styles['item-underline']}></hr>
          </div>
          <div className={styles['item-container']}>
            <label className={styles['item-name']}>Test</label>
            {renderTestSelect()}
            <hr className={styles['item-underline']}></hr>
          </div>
          <div className={styles['item-container']}>
            <label className={styles['item-name']}>Record Label</label>
            <Input
              allowClear
              className={styles['item-select']}
              onChange={(event) => setSelectedLabel(event.target.value)}
              onPressEnter={(e) => e.target.blur()}
              placeholder="Label"
              value={selectedLabel}
            />
            <hr className={styles['item-underline']}></hr>
          </div>
          <div className={styles['break']} />
          <div className={styles['item-container']}>
            <Button
              disabled={!selectedExamineeID || !selectedLabel || !selectedTestID}
              loading={isStarting}
              onClick={startTest}
              type="primary"
            >
              Start Test
            </Button>
          </div>
        </div>
      </div>
    </Dashboard>
  );
};

export default TestSessionPage;
